import { ToggleButton, ToggleButtonBar, ToggleButtonBarEvent } from '@acies/ui';
import type { DrawingSide } from '../../../domain/drawings';
import { ConfiguratorModel } from '../configurator.model';
import FrontBackToggleButtonBarEvent from './front-back-toggle-button-bar-event';

/** The order the buttons sit in, which is the one thing that turns an index into a side. */
const SIDES: ReadonlyArray<DrawingSide> = ['outside', 'inside'];

export default class FrontBackToggleButtonBar extends ToggleButtonBar {
	/**
	 * Whether there is a drawing to turn around.
	 *
	 * Proxied to both buttons rather than held here, the way `IconTitleSection` proxies its icon and its text to its header: `ToggleButtonBar` extends `Container`, which has no notion of being enabled, and what a user can and cannot press is the buttons themselves.
	 *
	 * Read off the first of them, because the two are only ever set together.
	 */
	public set enabled(value: boolean) {
		this.front.enabled = value;
		this.back.enabled = value;
	}

	public get enabled(): boolean {
		return this.front.enabled;
	}

	#front!: ToggleButton;
	#back!: ToggleButton;

	public constructor() {
		super();
		// the listener is registered on the model, so it would otherwise run in its scope rather
		// than in this one
		this.drawingChanged = this.drawingChanged.bind(this);
		this.selectedIndexChanged = this.selectedIndexChanged.bind(this);
		// Subscribed here rather than on connect, because an Observable tells its listeners about a
		// change and not about the value it already holds.
		ConfiguratorModel.currentDrawing.subscribe(this.drawingChanged);
		// Listened for on itself: ToggleButtonBar dispatches this, and the bar is where an index
		// still means something.
		this.addEventListener(ToggleButtonBarEvent.SELECTED_INDEX_CHANGED, this.selectedIndexChanged as EventListener);
	}

	public get front(): ToggleButton {
		if (!this.#front) {
			this.#front = new ToggleButton();
			this.#front.text = 'Udvendig';
		}
		return this.#front;
	}

	public get back(): ToggleButton {
		if (!this.#back) {
			this.#back = new ToggleButton();
			this.#back.text = 'Indvendig';
		}
		return this.#back;
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.front, this.back]);
		this.drawingChanged(ConfiguratorModel.currentDrawing.value);
	}

	/**
	 * Unsubscribes, because the model outlives the bar.
	 *
	 * `ConfiguratorModel` is a module constant, so a listener it still holds is a bar that has been removed and cannot be collected.
	 */
	protected override disconnected(): void {
		super.disconnected();
		ConfiguratorModel.currentDrawing.unsubscribe(this.drawingChanged);
	}

	/**
	 * Follows whether there is a drawing at all.
	 *
	 * There is nothing to turn around when no drawing is on screen — no position selected, one the engine has not priced, or one it could not price — so the buttons say so by being unpressable rather than by pretending to work.
	 *
	 * Which side is chosen is deliberately left alone. It is the user's standing preference rather than a property of the drawing in front of them: someone comparing the inside of one position with the inside of the next should not have to ask for it again each time they select one. `enabled` does not touch
	 * `selected`, so the choice survives a drawing coming and going.
	 */
	private drawingChanged(drawing: SVGSVGElement | null): void {
		this.enabled = drawing !== null;
	}

	/**
	 * Says which side was asked for, rather than which button was pressed.
	 *
	 * The index is translated here and nowhere else. An index outside the two buttons cannot happen from a press, and would mean nothing if it did, so it is left alone rather than reported as one side or the other.
	 *
	 * The bar's own event is stopped before anything else happens. Every component event bubbles, so one that has been answered with an event of its own must not carry on up: a listener above this bar would otherwise also hear the raw index from any other toggle bar nested below it, and act on a press that had nothing
	 * to do with a drawing. Stopped even when the index means nothing, because it has still been dealt with here.
	 */
	private selectedIndexChanged(event: ToggleButtonBarEvent): void {
		event.stopPropagation();
		const side = SIDES.at(event.selectedIndex);
		if (side === undefined) {
			return;
		}
		this.dispatchEvent(new FrontBackToggleButtonBarEvent(side));
	}
}
customElements.define('ac-front-back-toggle-button-bar', FrontBackToggleButtonBar);
